import { UserRole } from 'types';

import { DOFIN_READ_ROLES } from '../Dofinansowanie/useDofinPermissions';

/**
 * Role sets that gate palette commands / actions and the search fan-out. These
 * mirror the visibility rules in `organisms/Menu/MenuContainer` and
 * `router/AppRouter#getRoutesByRole` so the palette never surfaces a destination
 * the current operator has no route to.
 */
export const STUDENT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * Коммуникатор (`/communicator`) — зеркало `COMM_ACCESS_ROLES` бэкенда
 * (= `MANAGEMENT_ROLES`, с HR-менеджером). Отдельно от `STUDENT_ROLES`: HR
 * ведёт переписку из таск-трекера, но раздела «Студенты» у него нет. Одна
 * константа на маршрут, меню, палитру и `g → k`.
 */
export const COMMUNICATOR_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.HRMANAGER,
];

/**
 * Таск-трекер (`/dashboard`) — зеркало маршрута в `AppRouter`: роли студентов
 * и HR-менеджер (доска задач HR, ТЗ 0014). Одна константа на меню, палитру и
 * `g → d`, чтобы у HR пункт не пропадал, пока маршрут открыт.
 */
export const TASK_TRACKER_ROLES: UserRole[] = [...COMMUNICATOR_ROLES];

/**
 * Кто ОТКРЫВАЕТ карточку студента (`/students/:id/details`) — зеркало
 * маршрута `studentDetail` в `AppRouter`: `STUDENT_ROLES` и методист. У HR
 * маршрута нет — ссылки «Открыть карточку» в коммуникаторе ему не рисуются.
 */
export const STUDENT_CARD_ROLES: UserRole[] = [
  ...STUDENT_ROLES,
  UserRole.METHODIST,
];

/**
 * Кто ПРАВИТ «Обучение» из палитры: создать группу и преподавателя, править
 * группу, опубликовать в LMS. Методиста здесь нет намеренно: группу и журнал
 * он видит только на чтение (ТЗ 0026). Раздел «Преподаватели» — своя
 * константа `TEACHER_PROFILE_ROLES`.
 */
export const EDUCATION_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * ТЗ 0093: кто ОТКРЫВАЕТ «Обучение» — курсы, группы, журналы
 * (`/education/…`). Зеркало маршрута `education` в `AppRouter`: одна
 * константа на меню, палитру, `g → …` и саму страницу. До ТЗ 0093 у меню был
 * свой список с методистом, а у палитры — без него: методист видел пункт
 * меню, но не находил команду.
 */
export const EDUCATION_SECTION_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.METHODIST,
  UserRole.HRMANAGER,
];

/**
 * Кто ОТКРЫВАЕТ карточку курса (`/education/courses/:courseId`) — зеркало
 * маршрута `courseDetail` в `AppRouter`: Admin, AccountManager, методист,
 * HR-менеджер. Перепроверка G4: связь задачи «курс» вела в никуда тех, у
 * кого маршрута нет.
 *
 * #132: + продажи и финансист — ради вкладки «Цены» (цена курса по тарифу).
 * Программа и навыки им не видны: их запросы — `CAREER_ROLES` бэкенда.
 */
export const COURSE_CARD_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
  UserRole.HRMANAGER,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * Служебная часть досье преподавателя — зеркало `TEACHER_CARD_ROLES` бэкенда
 * (`teachers/schema/teachers_query.py`, `core.permissions.
 * erp_teacher_card_viewer`): Admin, AccountManager, SalesManager, HRManager.
 * Им — найм (`canReadTeacherDossier`), нагрузка, ставки и реквизиты, рейтинг,
 * внутренняя заметка, именные отзывы студентов, заявки. Кто вообще ОТКРЫВАЕТ
 * досье — `TEACHER_PROFILE_ROLES` ниже.
 */
export const TEACHER_CARD_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * Кто ОТКРЫВАЕТ преподавателей — список (`/teachers`) и карточку
 * (`/teachers/:id/details`). Зеркало `TEACHER_PROFILE_ROLES` бэкенда
 * (корни `teacher(teacherId)`, `allTeachers`, `metaTeachers`): роли карточки и
 * методист (решение владельца 09.10, ТЗ 0104 — методист работает с разборами
 * занятий преподавателя). Методисту сервер отдаёт только публичную часть:
 * служебные поля пусты, найм закрыт — вкладки досье гасят себя по
 * `useTeacherPermissions`. Одна константа на маршрут, меню, палитру, `g → t`,
 * связи задач и ссылки «открыть карточку». Финансисту преподавателей сервер
 * не отдаёт — имя у него простым текстом.
 */
export const TEACHER_PROFILE_ROLES: UserRole[] = [
  ...TEACHER_CARD_ROLES,
  UserRole.METHODIST,
];

export const FINANCE_VIEW_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * Расходы (`/finances/expenses`, «Добавить расход», расходы на дашборде
 * финансов и на вкладке «Финансы» группы) — зеркало `EXPENSES_ACCESS_ROLES`
 * бэкенда (`expenses/schema/schema.py`): Admin, FinanceManager. Аккаунт-
 * менеджера здесь нет по решению владельца 06.10.2026: сервер расходы ему не
 * отдаёт, и пункт меню вёл на экран из одних отказов.
 */
export const FINANCE_EXPENSE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
];

export const FINANCE_PLAN_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
];

export const ADMIN_ROLES: UserRole[] = [UserRole.ADMIN];

/**
 * ТЗ 0074 / 0093: настройки робота блокировки должников — зеркало
 * `PAYMENT_ACCESS_ROLES` бэкенда (Admin, FinanceManager, AccountManager,
 * SalesManager). Править можно тому, кому разрешит сервер (`canEdit`).
 */
export const PAYMENT_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * ТЗ 0023: «Подключённые ИИ» — зеркало `MANAGEMENT_ROLES` бэкенда, куда
 * входит и HR-менеджер (не `STUDENT_ROLES`).
 */
export const MCP_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.HRMANAGER,
];

export const ZOOM_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

// Survey constructor (spec 0011) — same managers who build/attach surveys and
// see results; mirrors the `/surveys` route + menu gate.
export const SURVEY_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

// Career centre (spec 0018) — mirrors the `/career-center` route gate and the
// menu leaf. Note it carries HRManager, unlike SURVEY_ROLES.
export const CAREER_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.HRMANAGER,
  UserRole.METHODIST,
];

/**
 * Со-финансирование (ТЗ 0024) — зеркало `DOFIN_READ_ROLES` на бэкенде.
 *
 * Совпадает по составу со `STUDENT_ROLES`, но объявлено отдельно намеренно:
 * это разные правила, и когда одно из них поменяется, менять придётся именно
 * его, а не оба сразу. FinanceManager (`FINANCIER`) входит сюда **только на
 * чтение** — записи бэкенд ему не даёт.
 *
 * ТЗ 0093: это ТОТ ЖЕ список, что у `useDofinPermissions().canRead` и
 * раздела «Настройки → Письма дофинансирования», — не копия.
 */
export const DOFIN_ROLES: UserRole[] = DOFIN_READ_ROLES;

export const PERFORMANCE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.HRMANAGER,
];

/**
 * ТЗ 0026: кто может ПРАВИТЬ отметку посещаемости.
 *
 * Зеркало ролей `updateAttendance` на бэкенде. Методиста здесь намеренно нет:
 * ему открыли журнал на чтение, и без этого списка он кликал бы по чекбоксу
 * и упирался в PermissionDenied.
 */
export const ATTENDANCE_EDIT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * `true` when `allowed` is undefined (visible to any logged-in operator) or the
 * operator holds at least one of the allowed roles.
 */
export const visibleFor = (roles: UserRole[], allowed?: UserRole[]): boolean =>
  !allowed || roles.some((role) => allowed.includes(role));
